import React from "react"
import {
  ActivityIndicator,
  AppState,
  AppStateStatus,
  Dimensions,
  LayoutChangeEvent,
  PixelRatio,
  Platform,
  SectionList,
  StyleSheet,
  View,
  ViewStyle,
} from "react-native"

import { useQueryClient } from "@tanstack/react-query"
import { observer } from "mobx-react-lite"
import { TMessage } from "types/transcript"

import { BrandBanner } from "src/components/BrandBanner"
import { ConversationHeader } from "src/components/SidekickChat/ConversationHeader"
import { MessageBubble } from "src/components/SidekickChat/MessageBubble"
import { useAuth } from "src/context/AuthContext"
import { useChat } from "src/context/ChatContext"
import { useRole } from "src/context/RoleContext"
import { useTyping } from "src/context/TypingContext"
import { useVisemes } from "src/context/VisemeContext"
import { useWebsocket } from "src/context/WebsocketContext"
import { useAppTheme } from "src/hooks/useAppTheme"
import { TChatSection, useGroupedChatMessages } from "src/hooks/useGroupedChatMessages"
import { useWebLayoutConstraints } from "src/hooks/useWebLayoutConstraints"
import { useStores } from "src/models"
import {
  fetchMorePastConversations,
  getConversationsHasMore,
  getSynchronizeAllConversationsFromQueryStatus,
  synchronizeAllConversationsFromQuery,
  useCloseConversationStatus,
  useCurrentConversation,
} from "src/queries/useConversation"
import { useResetDemoStatus } from "src/queries/useUser"
import { ThemedStyle } from "src/theme"
import { computeMessageKey } from "src/utils/messageUtils"
import { SidekickProgress } from "./SidekickProgress"
import { TypingIndicator } from "./TypingIndicator"
import { WebsocketConnectionIndicator } from "./WebsocketConnectionIndicator"

export interface SidekickChatProps {
  messagesOverride?: TMessage[]
  onMorePress: () => void
  onLayoutFooter?: ((event: LayoutChangeEvent) => void) | undefined
  onFeedbackPress?: (message: TMessage) => void
  topLayoutHeight?: number
  /** Invoked when the brand header is tapped (e.g. to open the content library). */
  onBrandPress?: () => void
}

const ChatHeader = () => (
  <>
    <SidekickProgress variant="chat" />
    <TypingIndicator />
  </>
)

export const SidekickChat = observer((props: SidekickChatProps) => {
  const { themed, theme } = useAppTheme()
  const { canvasLeft, canvasWidth } = useWebLayoutConstraints()

  // On web: widen the SectionList's scroll container so the scrollbar sits at the
  // screen's right edge rather than the right edge of the 720 px canvas column.
  // We walk up from the chat wrapper to the LayoutWrapper boundary and set
  // overflow: visible on each ancestor so the wider scroll container isn't clipped.
  React.useLayoutEffect(() => {
    if (Platform.OS !== "web" || canvasLeft <= 0) return

    const chatEl = document.getElementById("sk-chat-messages")
    if (!chatEl) return

    const layoutWrapper = document.getElementById("sk-layout-wrapper")
    const overriddenEls: HTMLElement[] = [chatEl]
    chatEl.style.overflow = "visible"

    let ancestor = chatEl.parentElement
    while (ancestor && ancestor !== layoutWrapper?.parentElement) {
      ancestor.style.overflow = "visible"
      overriddenEls.push(ancestor)
      ancestor = ancestor.parentElement
    }

    // Find the SectionList's actual scroll container by looking for the first
    // descendant div that the browser reports as scrollable (overflow auto/scroll).
    const findScrollContainer = (el: HTMLElement): HTMLElement | null => {
      for (const child of Array.from(el.children)) {
        const childEl = child as HTMLElement
        const style = window.getComputedStyle(childEl)
        if (style.overflowY === "auto" || style.overflowY === "scroll") {
          return childEl
        }
        const found = findScrollContainer(childEl)
        if (found) return found
      }
      return null
    }

    const scrollContainer = findScrollContainer(chatEl)
    let contentContainer: HTMLElement | null = null

    if (scrollContainer) {
      scrollContainer.style.width = `calc(100% + ${canvasLeft}px)`
      scrollContainer.style.overflowX = "hidden"
      contentContainer = scrollContainer.firstElementChild as HTMLElement | null
      if (contentContainer) {
        contentContainer.style.paddingRight = `${canvasLeft}px`
      }
    }

    return () => {
      overriddenEls.forEach((el) => {
        el.style.overflow = ""
      })
      if (scrollContainer) {
        scrollContainer.style.width = ""
        scrollContainer.style.overflowX = ""
      }
      if (contentContainer) {
        contentContainer.style.paddingRight = ""
      }
    }
  })

  const { awaitingUserSentence, awaitingESKResponse } = useTyping()
  const {
    typingStore: { incomingMessage },
  } = useStores()
  const { sendJsonMessage: wsSendMessage } = useWebsocket()
  const { isMuted } = useVisemes()

  const lastFontScaleRef = React.useRef(PixelRatio.getFontScale())
  const [listRemountKey, setListRemountKey] = React.useState(0)
  const [chatWidth, setChatWidth] = React.useState<number | null>(null)

  // Primary font scale detector: Dimensions.addEventListener fires when Android delivers
  // the updated fontScale to the JS bridge, which happens AFTER AppState 'active'.
  // This triggers a SectionList remount so VirtualizedList item heights are re-measured
  // at the new scale, preventing historical message bubbles from being cut off.
  React.useEffect(() => {
    const subscription = Dimensions.addEventListener("change", ({ window }) => {
      const newFontScale = window.fontScale
      if (newFontScale !== lastFontScaleRef.current) {
        lastFontScaleRef.current = newFontScale
        setListRemountKey((k) => k + 1)
      }
    })
    return () => subscription.remove()
  }, [])

  const {
    startNewConversation,
    startConversationStatus,
    isSwitchingSidekick,
    waitingForWelcomeMessage,
  } = useChat()
  const { isSwitchingRole } = useRole()

  const chatRef = React.useRef<SectionList<TMessage, TChatSection>>(null)

  const { data: currentConversation, isPending } = useCurrentConversation()
  const { data: resetDemoStatus } = useResetDemoStatus()
  const { data: closeConversationStatus } = useCloseConversationStatus()
  const { sections, isLoading: _isLoading } = useGroupedChatMessages()
  const { api } = useAuth()
  const queryClient = useQueryClient()

  const [isLoadingMore, setIsLoadingMore] = React.useState(false)
  const [hasMoreConversations, setHasMoreConversations] = React.useState(() =>
    getConversationsHasMore(),
  )

  const trySynchronizeChatHistory = React.useCallback(async () => {
    const startConversationStatus = queryClient.getQueryState(["startConversationStatus"])?.status
    const closeConversationStatus = queryClient.getQueryState(["closeConversationStatus"])?.status
    const isSynchronizing = getSynchronizeAllConversationsFromQueryStatus()

    const isConversationHistoryMutating =
      startConversationStatus === "pending" ||
      closeConversationStatus === "pending" ||
      isSynchronizing ||
      isSwitchingSidekick

    if (!isConversationHistoryMutating) {
      await synchronizeAllConversationsFromQuery(api, queryClient)
      // After a fresh sync (page 1), reset the hasMore flag so the list knows
      // whether older pages may still be available.
      setHasMoreConversations(getConversationsHasMore())
    }
  }, [api, isSwitchingSidekick, queryClient])

  // Fetch the next page of conversation history when the user scrolls to the
  // top of the inverted list (i.e. the oldest visible messages).
  const handleLoadMore = React.useCallback(async () => {
    if (isLoadingMore || !hasMoreConversations) return
    setIsLoadingMore(true)
    try {
      await fetchMorePastConversations(api, queryClient)
      setHasMoreConversations(getConversationsHasMore())
    } finally {
      setIsLoadingMore(false)
    }
  }, [api, isLoadingMore, hasMoreConversations, queryClient])

  // Sync on mount so 404 from server clears MMKV and allows start_conversation (AppState "change" may not fire for initial "active")
  React.useEffect(() => {
    trySynchronizeChatHistory().catch((e) => {
      console.warn("[SidekickChat] sync on mount rejected", e)
    })
  }, [trySynchronizeChatHistory])

  // Synchronize the chat history when the app comes to the foreground
  React.useEffect(() => {
    const subscription = AppState.addEventListener("change", (nextAppState: AppStateStatus) => {
      if (nextAppState === "active") {
        // Fallback font scale check: catches cases where the Dimensions event
        // hasn't fired yet by the time the app foregrounds.
        const currentFontScale = PixelRatio.getFontScale()
        if (currentFontScale !== lastFontScaleRef.current) {
          lastFontScaleRef.current = currentFontScale
          setListRemountKey((k) => k + 1)
        }
        trySynchronizeChatHistory().catch((e) => {
          console.warn("[SidekickChat] sync on foreground rejected", e)
        })
        wsSendMessage({
          type: "activity_status",
          isAudioOn: !isMuted,
          is_active: true,
        })
      } else if (nextAppState === "background") {
        // Only a genuine `background` transition means the user left the app.
        // The transient `inactive` (and `unknown`) states fire while the app is
        // still in the foreground — e.g. taking a screenshot, presenting the
        // share sheet/markup, pulling Control Center, peeking the app switcher,
        // or an incoming notification banner. Reporting `is_active: false` for
        // those caused the server to abandon an in-flight "thinking" response
        // (no reply was ever delivered) and to needlessly pause active speech.
        wsSendMessage({
          type: "activity_status",
          isAudioOn: !isMuted,
          is_active: false,
        })
      }
    })

    return () => {
      subscription.remove()
    }
  }, [trySynchronizeChatHistory, wsSendMessage, isMuted])

  // Get the conversation data and try to start a new conversation if the server doesn't return one.
  // We do not block on isSynchronizing: sync can take a long time (N+1 past conversation fetches),
  // and when we already have no current conversation we can send start_conversation immediately.
  React.useEffect(() => {
    if (
      !currentConversation &&
      !isPending &&
      !startConversationStatus.isPending &&
      !resetDemoStatus.isPending &&
      !closeConversationStatus.isPending &&
      !isSwitchingRole &&
      !isSwitchingSidekick
    ) {
      startNewConversation()
    }
  }, [
    currentConversation,
    isPending,
    resetDemoStatus.isPending,
    closeConversationStatus.isPending,
    isSwitchingRole,
    startConversationStatus.isPending,
    startNewConversation,
    isSwitchingSidekick,
  ])

  const [expandedMessageKey, setExpandedMessageKey] = React.useState<string | null>(null)
  // Track if user has manually selected a message (prevents auto-expand from overriding)
  const userSelectedMessageRef = React.useRef(false)

  const handleMessageExpand = React.useCallback((message: TMessage) => {
    const messageKey = computeMessageKey(message)
    userSelectedMessageRef.current = true
    setExpandedMessageKey(messageKey)
  }, [])

  const handleChatLayout = React.useCallback((event: LayoutChangeEvent) => {
    const { width } = event.nativeEvent.layout
    // Last-resort fallback: by the time layout fires the bridge always has the
    // correct fontScale, so catch any change missed by earlier checks.
    const currentFontScale = PixelRatio.getFontScale()
    if (currentFontScale !== lastFontScaleRef.current) {
      lastFontScaleRef.current = currentFontScale
      setListRemountKey((k) => k + 1)
    }
    setChatWidth(width)
  }, [])

  // Auto-expand most recent message logic
  // With inverted list, newest message is first section (index 0), first item (index 0)
  const newestSection = sections[0]
  const mostRecentMessage = newestSection?.data[0]
  const mostRecentMessageKey = mostRecentMessage ? computeMessageKey(mostRecentMessage) : null

  React.useEffect(() => {
    // Skip auto-expand if user has manually selected a different message
    if (userSelectedMessageRef.current) {
      return
    }

    if (mostRecentMessage && mostRecentMessageKey) {
      if (mostRecentMessageKey !== expandedMessageKey) {
        if (!awaitingUserSentence && !awaitingESKResponse) {
          setExpandedMessageKey(mostRecentMessageKey)
        }
      }
    }
  }, [
    mostRecentMessage,
    mostRecentMessageKey,
    awaitingUserSentence,
    awaitingESKResponse,
    expandedMessageKey,
  ])

  // Reset user selection flag when a new message arrives (allow auto-expand of new messages)
  const prevMostRecentMessageKey = React.useRef(mostRecentMessageKey)
  React.useEffect(() => {
    if (mostRecentMessageKey !== prevMostRecentMessageKey.current) {
      // A new message arrived - reset user selection so it can auto-expand
      userSelectedMessageRef.current = false
      prevMostRecentMessageKey.current = mostRecentMessageKey
    }
  }, [mostRecentMessageKey])

  React.useEffect(() => {
    if (awaitingUserSentence || awaitingESKResponse) {
      setExpandedMessageKey(null)
      // Reset user selection when waiting for response
      userSelectedMessageRef.current = false
    }
  }, [awaitingUserSentence, awaitingESKResponse])

  // Track message count to detect when data has changed
  const lastMessageCountRef = React.useRef(0)

  // Track total message count
  const totalMessageCount = React.useMemo(() => {
    return sections.reduce((acc, section) => acc + section.data.length, 0)
  }, [sections])

  const isTyping =
    awaitingUserSentence ||
    awaitingESKResponse ||
    incomingMessage.length > 0 ||
    waitingForWelcomeMessage ||
    isSwitchingSidekick

  const scrollToBottom = React.useCallback((animated = true) => {
    try {
      const list = chatRef.current
      if (!list) return

      // In an inverted list, offset 0 is the visual bottom (where the header is).
      // We try multiple ways to reach the underlying scroll mechanism because
      // SectionList doesn't always expose scrollToOffset directly.
      // @ts-ignore
      const scrollResponder = list.getScrollResponder?.()
      if (scrollResponder?.scrollTo) {
        scrollResponder.scrollTo({ x: 0, y: 0, animated })
      } else {
        // Fallback for types that might not have getScrollResponder or if it fails
        list.scrollToLocation({
          sectionIndex: 0,
          itemIndex: 0,
          animated,
          viewPosition: 0,
        })
      }
    } catch {
      // Fallback
    }
  }, [])

  // Autoscroll to bottom when new messages arrive, thinking starts, or keyboard state changes
  React.useEffect(() => {
    if (totalMessageCount > lastMessageCountRef.current || isTyping) {
      // Use multiple timeouts to ensure we catch the layout as it settles
      // especially after keyboard transitions (250ms).
      // Note: We don't include incomingMessage in dependencies here to avoid
      // constantly resetting these timeouts during streaming; onContentSizeChange
      // handles the streaming growth.
      const t1 = setTimeout(() => scrollToBottom(), 100)
      const t2 = setTimeout(() => scrollToBottom(), 250)
      const t3 = setTimeout(() => scrollToBottom(), 500)

      lastMessageCountRef.current = totalMessageCount
      return () => {
        clearTimeout(t1)
        clearTimeout(t2)
        clearTimeout(t3)
      }
    }
    return undefined
  }, [totalMessageCount, isTyping, scrollToBottom])

  const handleContentSizeChange = React.useCallback(() => {
    if (isTyping) {
      scrollToBottom()
    }
  }, [isTyping, scrollToBottom])

  const renderItem = ({ item }: { item: TMessage }) => {
    const messageKey = computeMessageKey(item)

    // On web, fall back to the constrained canvas width (available synchronously)
    // until onLayout reports the measured width. This avoids a first-paint flash
    // where bubbles size to the full viewport before snapping to the canvas width.
    const resolvedContainerWidth = chatWidth ?? (Platform.OS === "web" ? canvasWidth : undefined)

    return (
      <MessageBubble
        item={item}
        onFeedbackPress={props.onFeedbackPress}
        expanded={messageKey === expandedMessageKey}
        onExpand={handleMessageExpand}
        containerWidth={resolvedContainerWidth}
      />
    )
  }

  const renderSectionHeader = ({ section }: { section: TChatSection }) => {
    const shouldShowPendingCurrentHeader =
      section.isCurrent &&
      (awaitingUserSentence ||
        awaitingESKResponse ||
        incomingMessage.length > 0 ||
        waitingForWelcomeMessage ||
        isSwitchingSidekick)

    if (section.data.length === 0 && !shouldShowPendingCurrentHeader) return null

    return (
      <View style={$sectionHeaderWrapper}>
        {/* Separator line visually ABOVE the header */}
        <View style={themed($sectionSeparator)} />
        <ConversationHeader
          sidekickName={section.sidekickName}
          avatarId={section.avatarId}
          mskAvatarId={section.mskAvatarId}
          isCurrent={section.isCurrent}
          topic={section.topic}
          date={section.date}
          contentModuleName={section.contentModuleName}
        />
      </View>
    )
  }

  return (
    <>
      <View style={[$flex, { backgroundColor: theme.colors.background }]}>
        <WebsocketConnectionIndicator useSafeArea />
        <View style={chatStyles.headerBrandRow}>
          <View style={chatStyles.headerBrandSpacer} />
          <View style={chatStyles.headerBrandCenter}>
            <BrandBanner key={`brand-banner-${listRemountKey}`} onPress={props.onBrandPress} />
          </View>
          <View style={chatStyles.headerBrandSpacer} />
        </View>

        {/* Chat messages - takes up remaining space */}
        <View nativeID="sk-chat-messages" style={$flexOverflow} onLayout={handleChatLayout}>
          {resetDemoStatus.isPending ? (
            <ActivityIndicator size="large" />
          ) : (
            <>
              <SectionList
                key={`chat-list-${listRemountKey}`}
                keyboardDismissMode="on-drag"
                // @ts-ignore
                ref={chatRef}
                inverted={true}
                contentContainerStyle={themed($contentContainer)}
                style={themed($flatList)}
                sections={sections}
                stickySectionHeadersEnabled={false}
                renderItem={renderItem}
                renderSectionFooter={renderSectionHeader}
                keyExtractor={(item: TMessage) => computeMessageKey(item)}
                ListHeaderComponent={ChatHeader}
                // In an inverted list, ListFooterComponent renders at the top
                // (the oldest-message end). On web only: show a spinner while
                // overflow conversations beyond the localStorage cap are being fetched.
                ListFooterComponent={
                  Platform.OS === "web" && isLoadingMore ? (
                    <ActivityIndicator style={$loadMoreIndicator} />
                  ) : null
                }
                onContentSizeChange={handleContentSizeChange}
                // For inverted list, newest content appears at bottom automatically
                initialNumToRender={30}
                maxToRenderPerBatch={20}
                windowSize={11}
                // Web only: the localStorage cap means older conversations may not be
                // in MMKV. onEndReached (fires at the oldest-message end of the
                // inverted list) fetches them from the in-memory extended-past cache.
                onEndReached={Platform.OS === "web" ? handleLoadMore : undefined}
                onEndReachedThreshold={Platform.OS === "web" ? 0.2 : undefined}
              />
            </>
          )}
        </View>
      </View>
    </>
  )
})

const $contentContainer: ThemedStyle<ViewStyle> = ({ colors }) => ({
  backgroundColor: colors.background,
})

const $flatList: ThemedStyle<ViewStyle> = ({ colors }) => ({
  backgroundColor: colors.background,
})

const $flex: ViewStyle = {
  flex: 1,
}

const $flexOverflow: ViewStyle = {
  ...$flex,
  overflow: "hidden",
}

const $sectionHeaderWrapper: ViewStyle = {
  alignSelf: "stretch",
}

const $loadMoreIndicator: ViewStyle = {
  paddingVertical: 16,
}

const $sectionSeparator: ThemedStyle<ViewStyle> = ({ spacing, colors }) => ({
  height: 1,
  backgroundColor: colors.sectionSeparator,
  marginHorizontal: spacing.lg,
  marginVertical: spacing.md,
})

const chatStyles = StyleSheet.create({
  headerBrandRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 16,
  },
  headerBrandSpacer: {
    width: 45,
  },
  headerBrandCenter: {
    flex: 1,
    alignItems: "center",
  },
})
